import { createApi } from '@reduxjs/toolkit/dist/query/react';
import _ from 'lodash';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import {
  groupsListSchema,
  groupsSchema,
  GroupStructure,
  NormalizedGroup,
} from './GroupAdapter';
import { GroupAPIResponse, GroupCreateAPI } from './group.model';
import GroupsCalls from './GroupCalls';
import { normalize } from 'normalizr';
import Util from '../../../util/Util';
import { IconUpload } from '../groups.model';
import convertPromiseToQueryReturnValue from '../../../axios/convertPromiseToQueryReturnValue';
import { Base64 } from '../../../util/Base64';

export const GroupAPI = createApi({
  reducerPath: 'group',
  // 1 day
  keepUnusedDataFor: 60 * 60 * 24,
  baseQuery: axiosBaseQuery(),
  tagTypes: ['group-data'],
  endpoints(build) {
    return {
      getGroups: build.query({
        query: () => ({
          url: new Util().getGroupServiceURL(),
          method: 'GET',
        }),
        transformResponse: (data: GroupAPIResponse[]) =>
          normalize(data, groupsListSchema) as GroupStructure,
        providesTags: ['group-data'],
      }),
      getGroupById: build.query<NormalizedGroup | undefined, string>({
        query: (groupId: string) => ({
          url: `${new Util().getGroupServiceURL()}/${Base64.encode(groupId)}`,
          method: 'GET',
        }),
        transformResponse: (data: GroupAPIResponse) => {
          const normalized = normalize(data, groupsSchema) as {
            entities?: {
              groups?: Record<string, NormalizedGroup>;
            };
            result?: string;
          };

          const group =
            normalized.result && normalized.entities?.groups
              ? normalized.entities.groups[normalized.result]
              : undefined;

          return group;
        },
        providesTags: ['group-data'],
        async onQueryStarted(_groupId, { dispatch, queryFulfilled }) {
          try {
            const { data: group } = await queryFulfilled;

            if (!group?.id) return;

            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                if (!draft.entities?.groups?.[group.id]) {
                  draft.entities.groups[group.id] = group;
                  draft.result.push(group.id);
                }
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),

      putGroup: build.mutation({
        queryFn: (props: {
          groupId: string;
          groupData: Partial<GroupCreateAPI>;
          iconUpload?: IconUpload;
        }) => {
          const groupResponse = new GroupsCalls().putGroup(
            props.groupId,
            props.groupData,
            props.iconUpload
          );
          return convertPromiseToQueryReturnValue(groupResponse);
        },
        invalidatesTags: ['group-data'],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for mutation to succeed
            await queryFulfilled;
            // Pessimistic update the cache
            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                const group = draft.entities.groups?.[arg.groupId];
                if (group) {
                  // Update the group with the new data
                  Object.assign(group, arg.groupData);
                }
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),

      deleteDeviceGroup: build.mutation({
        query: (args: { groupId: string; commanderId: string }) => {
          const { groupId, commanderId } = args;
          return {
            url: `${new Util().getGroupServiceURL()}/${Base64.encode(
              groupId
            )}/devices/${Base64.encode(commanderId)}`,
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        invalidatesTags: ['group-data'],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for mutation to succeed
            await queryFulfilled;
            // Pessimistic update the cache
            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                const group = draft.entities.groups?.[arg.groupId];
                if (group) {
                  group.devices = group.devices.filter(
                    (commanderId) => commanderId !== arg.commanderId
                  );
                }
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),
      getGroupsByWorkspaceId: build.query({
        query: (workspaceId: string) => ({
          url: `${new Util().getWorkSpaceManagementServiceURL()}/${Base64.encode(
            workspaceId
          )}/groups/tree`,
          method: 'GET',
        }),
        transformResponse: (data: GroupAPIResponse[]) =>
          normalize(data, groupsListSchema) as GroupStructure,
        providesTags: ['group-data'],
        async onQueryStarted(_workspaceId, { dispatch, queryFulfilled }) {
          try {
            const { data: workspaceGroups } = await queryFulfilled;
            const groups = Object.values(
              workspaceGroups?.entities?.groups || {}
            );

            if (!groups.length) return;

            dispatch(
              GroupAPI.util.updateQueryData('getGroups', undefined, (draft) => {
                draft.entities = draft.entities || { groups: {} };
                draft.entities.groups = draft.entities.groups || {};
                draft.result = draft.result || [];

                groups.forEach((group) => {
                  if (!group?.id) return;

                  draft.entities.groups[group.id] = group;

                  if (!draft.result.includes(group.id)) {
                    draft.result.push(group.id);
                  }
                });
              })
            );
          } catch (err) {
            /* empty */
          }
        },
      }),
    };
  },
});
